import { useEffect, useRef, useState } from 'react';

/** True while this tab is visible and its window has focus: the only time work lists should poll. */
export const isForegroundActive = (): boolean =>
  typeof document === 'undefined' ||
  (document.visibilityState === 'visible' && document.hasFocus());

/**
 * Tracks whether the window is in front (tab visible and window focused). Work lists poll only while this is true,
 * so a Hub left open behind another window or in a background tab makes no calls.
 */
export function useForegroundActive(): boolean {
  const [active, setActive] = useState(isForegroundActive);
  useEffect(() => {
    const update = () => setActive(isForegroundActive());
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
    };
  }, []);
  return active;
}

/**
 * When the window comes back in front, refetches once, and only if the data is older than `staleAfterMs`, so
 * switching windows never fires a burst of calls.
 */
export function useRefetchOnReturn(
  active: boolean,
  staleAfterMs: number,
  lastUpdatedAt: number | undefined,
  refetch: () => unknown,
): void {
  const latest = useRef({ lastUpdatedAt, refetch });
  latest.current = { lastUpdatedAt, refetch };
  const wasActive = useRef(active);
  useEffect(() => {
    const { lastUpdatedAt: at, refetch: run } = latest.current;
    if (active && !wasActive.current && (!at || Date.now() - at > staleAfterMs)) void run();
    wasActive.current = active;
  }, [active, staleAfterMs]);
}
